import React from 'react';
import { formatClockMmSs, formatClockMmSsTenths } from '../../../../helpers/countdownClock';
import {
    ActiveExclusionClock,
    periodPills,
    timingSnapshotFromMatch,
} from '../../../../helpers/matchTiming';
import { EventPeriodEnum, MatchDetailVm, MatchPlayerVm } from '../../../../utils/api';
import { OvertimeUiMode } from '../../../match-report/match-clock/periodLabels';

interface ScoreHeaderProps {
    match: MatchDetailVm;
    period: EventPeriodEnum;
    overtimeMode: OvertimeUiMode;
    timeRemaining: number;
    shotClock: number;
    clockRunning: boolean;
    exclusionClocks: ActiveExclusionClock[];
    onEditClock: () => void;
    onEditShot: () => void;
    onSetPeriod: (period: EventPeriodEnum, overtimeMode?: OvertimeUiMode) => void;
    onDismissExclusion: (eventId: number) => void;
}

interface PeriodDot {
    key: string;
    label: string;
    period: EventPeriodEnum;
    mode: OvertimeUiMode;
}

// Q1-Q4 plus PS (penalty shootout = our Overtime + penalty_shootout mode).
// Period pills follow the Match Timing snapshot: N regulation + always OT + PSO.
const formatClock = (totalSeconds: number): string => formatClockMmSsTenths(totalSeconds);

const abbr = (name?: string): string => {
    const letters = (name ?? '').replace(/[^A-Za-z]/g, '');
    return letters.slice(0, 3).toUpperCase() || '---';
};

const capOf = (players: MatchPlayerVm[], playerId: number): string => {
    const player = players.find((p) => p.id === playerId);
    if (!player) return '?';
    return String(player.capNumber || player.number);
};

const sideOf = (
    clock: ActiveExclusionClock,
    match: MatchDetailVm,
    players: MatchPlayerVm[]
): 'home' | 'away' | undefined => {
    const teamId = clock.teamId ?? players.find((p) => p.id === clock.playerId)?.teamId;
    if (teamId === match.homeTeamId) return 'home';
    if (teamId === match.awayTeamId) return 'away';
    return undefined;
};

const remainingLabel = (totalSeconds: number): string => {
    const whole = Math.max(0, Math.ceil(totalSeconds - 1e-9));
    return whole >= 60 ? formatClockMmSs(whole) : String(whole);
};

function ExclusionChips(props: {
    clocks: ActiveExclusionClock[];
    players: MatchPlayerVm[];
    onDismiss: (eventId: number) => void;
}): React.ReactElement {
    const { clocks, players, onDismiss } = props;
    return (
        <>
            {clocks.map((clock) => (
                <button
                    key={clock.eventId}
                    type="button"
                    className="m-sb__excl-item"
                    onClick={(): void => onDismiss(clock.eventId)}
                >
                    <span className="m-sb__excl-cap">{capOf(players, clock.playerId)}</span>
                    <span className="m-sb__excl-time">
                        - {remainingLabel(clock.remainingSeconds)}
                    </span>
                </button>
            ))}
        </>
    );
}

const ScoreHeader: React.FC<ScoreHeaderProps> = ({
    match,
    period,
    overtimeMode,
    timeRemaining,
    shotClock,
    clockRunning,
    exclusionClocks,
    onEditClock,
    onEditShot,
    onSetPeriod,
    onDismissExclusion,
}) => {
    const pills: PeriodDot[] = periodPills(timingSnapshotFromMatch(match).periodCount).map(
        (pill) => ({
            key: `${pill.label}-${pill.overtimeMode ?? 'reg'}`,
            label: pill.label,
            period: pill.periodId,
            mode: pill.overtimeMode ?? 'play',
        })
    );
    const isActive = (dot: PeriodDot): boolean =>
        dot.period === period &&
        (dot.period !== EventPeriodEnum.Overtime || dot.mode === overtimeMode);

    const players = (match.players as MatchPlayerVm[]) || [];
    const homeClocks = exclusionClocks.filter((clock) => sideOf(clock, match, players) === 'home');
    const awayClocks = exclusionClocks.filter((clock) => sideOf(clock, match, players) === 'away');

    return (
        <div className="m-sb">
            <div className="m-sb__top">
                <span className="m-sb__abbr">{abbr(match.homeTeamDisplayName)}</span>
                <div className="m-sb__periods">
                    {pills.map((dot) => (
                        <button
                            key={dot.key}
                            type="button"
                            className={`m-sb__dot${isActive(dot) ? ' is-active' : ''}`}
                            onClick={(): void => onSetPeriod(dot.period, dot.mode)}
                        >
                            {dot.label}
                        </button>
                    ))}
                </div>
                <span className="m-sb__abbr">{abbr(match.awayTeamDisplayName)}</span>
            </div>

            <div className="m-sb__mid">
                <span className="m-sb__score">{match.homeTeamGoalsTotal ?? 0}</span>
                <button
                    type="button"
                    className={`m-sb__clock${clockRunning ? ' is-running' : ' is-stopped'}`}
                    onClick={onEditClock}
                >
                    {formatClock(timeRemaining)}
                </button>
                <span className="m-sb__score">{match.awayTeamGoalsTotal ?? 0}</span>
            </div>

            <div className="m-sb__shot-row">
                <div className="m-sb__excl m-sb__excl--home">
                    <ExclusionChips
                        clocks={homeClocks}
                        players={players}
                        onDismiss={onDismissExclusion}
                    />
                </div>
                <button type="button" className="m-sb__shot" onClick={onEditShot}>
                    <span className="m-sb__shot-label">SHOT CLOCK</span>
                    <span className="m-sb__shot-value">{Math.ceil(shotClock)}</span>
                </button>
                <div className="m-sb__excl m-sb__excl--away">
                    <ExclusionChips
                        clocks={awayClocks}
                        players={players}
                        onDismiss={onDismissExclusion}
                    />
                </div>
            </div>
        </div>
    );
};

export default ScoreHeader;
